import { FC, useEffect, useRef, useState } from "react";
import cx from "classnames";

import { SettingsSections } from "./../../store/ui/types";
import useSettings from "./../../hooks/useSettings";
import { IconTypes } from "./../../libs/Icon";
import Button from "./../../libs/Button";
import General from "./General";
import Limits from "./Limits";
import Notification from "./Notification";
import SaveProgress from "./SaveProgress";
import Confirmation from "./Confirmation";

import styles from "./styles.module.scss";

const titles = {
    [SettingsSections.GENERAL]: "General Settings",
    [SettingsSections.LIMITS]: "Submission Limits",
    [SettingsSections.NOTIFICATION]: "Notifications",
    [SettingsSections.SAVE_PROGRESS]: "Save Progress",
    [SettingsSections.CONFIRMATION]: "Confirmation Settings",
};
const renderSections = (currentSection: SettingsSections) => {
    const sectionsNavigator = () => {
        switch (currentSection) {
            case SettingsSections.GENERAL: {
                return <General />;
            }
            case SettingsSections.LIMITS: {
                return <Limits />;
            }
            case SettingsSections.NOTIFICATION: {
                return <Notification />;
            }
            case SettingsSections.SAVE_PROGRESS: {
                return <SaveProgress />;
            }
            case SettingsSections.CONFIRMATION: {
                return <Confirmation />;
            }
            default:
                return null;
        }
    };
    return sectionsNavigator();
};
const SettingsDrawer: FC = () => {
    const { isOpen, onClose, currentSection, changeCurrentSection } = useSettings();
    const [show, setShow] = useState<boolean>(false);
    const settingsRef = useRef(null);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (isOpen) document.getElementById("blurable")!.style.filter = "blur(10px)";
        else document.getElementById("blurable")!.style.filter = "none";
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && show !== true) {
            setShow(true);
        }
    }, [isOpen, show, setShow]);

    if (!show) return null;

    const handleonClose = () => {
        if (isMounted) onClose();
    };

    return (
        <div
            className={cx(styles.overlay, {
                [styles.active]: isOpen,
                [styles.inactive]: !isOpen,
            })}
            onClick={handleonClose}
            onAnimationEnd={() => {
                if (!isOpen && show) {
                    setShow(false);
                }
            }}
        >
            <div className={styles.wrapper} onClick={e => e.stopPropagation()} ref={settingsRef}>
                <div className={cx(styles.content)}>
                    <Button icon={IconTypes.CLOSE} className={styles.closeButton} iconSize={20} onClick={onClose} />

                    <div className={styles.menu}>
                        <Button
                            leftIcon={IconTypes.SETTINGS}
                            leftIconSize={32}
                            text="General"
                            className={cx(styles.menuItem, {
                                [styles.active]: currentSection === SettingsSections.GENERAL,
                            })}
                            onClick={() => changeCurrentSection(SettingsSections.GENERAL)}
                        />

                        <Button
                            leftIcon={IconTypes.LIMITS}
                            leftIconSize={32}
                            text="Limits"
                            className={cx(styles.menuItem, {
                                [styles.active]: currentSection === SettingsSections.LIMITS,
                            })}
                            onClick={() => changeCurrentSection(SettingsSections.LIMITS)}
                        />

                        <Button
                            leftIcon={IconTypes.NOTIFICATION}
                            leftIconSize={32}
                            text="Notifications"
                            className={cx(styles.menuItem, {
                                [styles.active]: currentSection === SettingsSections.NOTIFICATION,
                            })}
                            onClick={() => changeCurrentSection(SettingsSections.NOTIFICATION)}
                        />

                        <Button
                            leftIcon={IconTypes.SAVE_PROGRESS}
                            leftIconSize={32}
                            text="Save Progress"
                            className={cx(styles.menuItem, {
                                [styles.active]: currentSection === SettingsSections.SAVE_PROGRESS,
                            })}
                            onClick={() => changeCurrentSection(SettingsSections.SAVE_PROGRESS)}
                        />

                        <Button
                            leftIcon={IconTypes.CONFIRMATION}
                            leftIconSize={32}
                            text="Confirmation"
                            className={cx(styles.menuItem, {
                                [styles.active]: currentSection === SettingsSections.CONFIRMATION,
                            })}
                            onClick={() => changeCurrentSection(SettingsSections.CONFIRMATION)}
                        />
                    </div>
                    <div className={styles.subcontent} onClick={e => e.stopPropagation()}>
                        <h5 className={styles.title}>{titles[currentSection]}</h5>

                        {renderSections(currentSection)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsDrawer;
